import { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import Raven from 'raven-js';
import { getAuthResult } from 'quartz-auth/service';
import { activateApp } from './activateApp';
import { GlobalErrorBoundary } from '../components/GlobalErrorBoundary';
import { ErrorPage } from '../components/ErrorPage';
import { AppRoot } from '../internal/root-renderer';
import { isPromiseLike } from '../internal/isPromiseLike';
import { getCurrentPackageVersion } from 'quartz/framework/environment/service';
import { reconfigure } from 'raven-hubspot/configure';
import { primaryTrackers } from 'usage-tracker-container/primaryTrackerState';
import { jsx as _jsx } from "react/jsx-runtime";
function GraniteLocalApp({
  history,
  activationResult,
  AppComponent,
  LayoutComponent,
  rhumbConfig,
  rhumbTrackVisibility,
  userId,
  softNavigationContext
}) {
  const [result, setResult] = useState(null);
  useEffect(() => {
    function handleResult(r) {
      if (r && 'redirect' in r) {
        window.location.href = r.redirect;
        return;
      }
      setResult(r);
    }
    if (isPromiseLike(activationResult)) {
      activationResult.then(handleResult).catch(error => {
        Raven.captureException(error);
        setResult(null);
      });
    } else {
      handleResult(activationResult);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (result && 'onAfterRender' in result) {
      var _result$onAfterRender;
      (_result$onAfterRender = result.onAfterRender) === null || _result$onAfterRender === void 0 || _result$onAfterRender.call(result);
    }
  }, [result]);
  if (result === null) {
    return null;
  }
  const content = LayoutComponent ? /*#__PURE__*/_jsx(LayoutComponent, {
    children: /*#__PURE__*/_jsx(AppComponent, Object.assign({}, result === null || result === void 0 ? void 0 : result.props))
  }) : /*#__PURE__*/_jsx(AppComponent, Object.assign({}, result === null || result === void 0 ? void 0 : result.props));
  return /*#__PURE__*/_jsx(AppRoot, {
    history: history,
    rhumbConfig: rhumbConfig,
    rhumbTrackVisibility: rhumbTrackVisibility,
    userId: userId,
    softNavigationContext: softNavigationContext,
    isGraniteEnabled: true,
    children: content
  });
}
export function createGraniteLocalApp(appName, historyBasename, props, runtime) {
  return {
    name: appName,
    copilotLayoutType: props.copilotLayoutType,
    async render({
      container,
      softNavigationContext
    }) {
      primaryTrackers.clear();
      reconfigure({
        project: appName,
        release: getCurrentPackageVersion()
      });
      const {
        history,
        basename
      } = runtime.historyManager.createLocalNestedHistory(historyBasename);
      runtime.historyManager.connect(appName, history, basename);
      const appResult = activateApp(props.adapter, getAuthResult(), props.prepareApp);
      await runtime.cssManager.switchTo(appName);
      const root = createRoot(container);
      let removeWindowListener;
      const handle = {
        appName,
        unmount: () => {
          var _removeWindowListener;
          root.unmount();
          runtime.historyManager.disconnect();
          (_removeWindowListener = removeWindowListener) === null || _removeWindowListener === void 0 || _removeWindowListener();
        }
      };
      const resultPromise = isPromiseLike(appResult) ? appResult : Promise.resolve(appResult);
      resultPromise.then(result => {
        if (result && !('redirect' in result) && result.onBeforeAppUnload) {
          const {
            onBeforeAppUnload
          } = result;
          // BeforeUnloadEvent satisfies SyntheticBeforeUnloadEvent.
          window.addEventListener('beforeunload', onBeforeAppUnload);
          removeWindowListener = () => window.removeEventListener('beforeunload', onBeforeAppUnload);
          handle.onBeforeAppUnload = event => {
            onBeforeAppUnload(event);
            if (!event.defaultPrevented) {
              var _removeWindowListener2;
              (_removeWindowListener2 = removeWindowListener) === null || _removeWindowListener2 === void 0 || _removeWindowListener2();
              removeWindowListener = undefined;
            }
          };
        }
      }).catch(() => {});
      root.render( /*#__PURE__*/_jsx(GlobalErrorBoundary, {
        ErrorComponent: ErrorPage,
        children: /*#__PURE__*/_jsx(GraniteLocalApp, {
          activationResult: resultPromise,
          history: history,
          softNavigationContext: softNavigationContext,
          AppComponent: props.AppComponent,
          LayoutComponent: props.LayoutComponent,
          rhumbConfig: props.rhumbConfig,
          rhumbTrackVisibility: props.rhumbTrackVisibility,
          userId: props.userId
        })
      }));
      return handle;
    }
  };
}